import { Fragment, useState } from "react";
import { FaBan, FaKey, FaLock, FaUnlock, FaUser } from "react-icons/fa";
import { BsThreeDots } from "react-icons/bs";
import Badge from "react-bootstrap/Badge";
import Dropdown from "react-bootstrap/Dropdown";

import { Paginated } from "@/@types/generics/paginated";
import { PermissionEnum } from "@/utils/enums/PermissionEnum";
import { formatIdentifier } from "@/utils/formatIdentifier";
import { UsersAccountAccess } from "@/services/app/userAccountService";
import { ModuleEnum } from "@/utils/enums/ModuleEnum";
import { Account } from "@/@types/app/Account";

import { TableSkeletons } from "@/components/TableSkeletons";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { Restrict } from "@/components/Restrict";
import { ModalProfessionalInformation } from "@/components/ModalProfessionalInformation";
import { ModalProfessionalUnitAccess } from "@/components/ModalProfessionalUnitAccess";
import { AccessBadge } from "@/components/AccessBadge/AccessBadge";

type Props = {
	isLoading: boolean;
	paginatedUsers: Paginated<UsersAccountAccess>;
	pageNumber: number;
	cities: Account[];
	selectedUf?: string;
	selectedIbgeCode?: string;
	selectedCnes?: string;
	selectedModule?: number;
	handleChangePageNumber: (pageNumber: number) => void;
	handleChangeAccess: (user: UsersAccountAccess) => void;
	handleConfirmRevoke: (user: UsersAccountAccess, uf: string, ibgeCode?: string) => void;
	refreshTable: () => void;
};

export function UsersTable({
	isLoading,
	paginatedUsers,
	pageNumber,
	selectedUf,
	selectedIbgeCode,
	selectedCnes,
	selectedModule,
	handleChangePageNumber,
	handleChangeAccess,
	handleConfirmRevoke,
	refreshTable,
}: Props) {
	const [selectedUser, setSelectedUser] = useState<UsersAccountAccess>({} as UsersAccountAccess);
	const [showModalProfessionalInformation, setShowModalProfessionalInformation] = useState(false);
	const [showModalProfessionalUnitAccess, setShowModalProfessionalUnitAccess] = useState(false);

	return (
		<>
			<table
				id="table-professionals"
				className="table align-middle table-row-dashed fs-7 fw-bold dataTable no-footer"
			>
				<thead>
					<tr className="text-center text-muted">
						<th className="min-w-125px">Status</th>
						<th>Nome</th>
						<th className="min-w-125px">Cpf</th>
						<th>Cargo</th>
						<th className="min-w-125px">Modulos</th>
						<th>Acessos</th>
						<th>Ações</th>
					</tr>
				</thead>
				<tbody className="table-tbody">
					{paginatedUsers.data ? (
						<Fragment>
							{paginatedUsers.data?.map((userRow: UsersAccountAccess, index) => (
								<tr key={index} className="border-bottom-1 border-dark">
									<td>
										{Boolean(userRow.isLocked) ? (
											<label className="alert alert-block">Bloqueado</label>
										) : Boolean(userRow.hasProfileActive) ? (
											<label className="alert alert-active my-2 ps-7 pe-7">
												Ativado
											</label>
										) : (
											<label className="alert alert-pending my-2 ps-7 pe-7">
												Pendente
											</label>
										)}
									</td>
									<td>{userRow.fullName} </td>
									<td className="text-center">
										{formatIdentifier(userRow.identifier)}
									</td>
									<td>{userRow.occupation}</td>
									<td className="text-center">
										{userRow.modules?.map((module, index) => (
											<Badge
												key={index}
												className="m-1"
												bg={`${
													module.id === ModuleEnum.ESUS_APS
														? "success"
														: module.id === ModuleEnum.SINTEGRADA
														? "primary"
														: null
												}`}
											>
												{module.description}
											</Badge>
										))}
									</td>
									<td className="text-center">
										<Restrict
											permissions={[
												PermissionEnum.SUPER_ADMIN,
												PermissionEnum.ESUS_STATE_PROFESSIONAL_MANAGEMENT,
											]}
										>
											<AccessBadge
												color="light"
												classNameLabel="mx-4 text-muted border border-muted"
												classNameCount="bg-secondary text-primary"
												label="Estado"
												count={userRow.accessByUF?.length || 0}
											/>
											<AccessBadge
												color="light"
												classNameLabel="mx-4 text-muted border border-muted"
												classNameCount="bg-secondary text-primary"
												label="Cidade"
												count={userRow.accessByMunicipality?.length || 0}
											/>
										</Restrict>
										<AccessBadge
											color="light"
											classNameLabel="mx-4 text-muted border border-muted"
											classNameCount="bg-secondary text-primary"
											label="Unidade"
											count={userRow.accessByCNES?.length || 0}
										/>
									</td>
									<td className="text-center">
										<Dropdown>
											<Dropdown.Toggle
												variant="secondary"
												id="dropdown-basic"
											>
												<BsThreeDots />
											</Dropdown.Toggle>

											<Dropdown.Menu>
												<Dropdown.Item
													className="d-flex align-items-center gap-2"
													onClick={() => {
														setSelectedUser(userRow);
														setShowModalProfessionalInformation(true);
													}}
												>
													<FaUser className="text-muted" />
													Perfil
												</Dropdown.Item>
												{!userRow.isLocked && (
													<Dropdown.Item
														className="d-flex align-items-center gap-2"
														onClick={() => {
															setSelectedUser(userRow);
															setShowModalProfessionalUnitAccess(
																true
															);
														}}
													>
														<FaKey className="text-muted" />{" "}
														Configuração de acesso
													</Dropdown.Item>
												)}
												<Restrict
													permissions={[
														PermissionEnum.ESUS_STATE_PROFESSIONAL_MANAGEMENT,
														PermissionEnum.ESUS_VIEW_PROFESSIONAL_MANAGEMENT,
														PermissionEnum.SINTEGRADA_PROFESSIONAL_MANAGEMENT,
													]}
												>
													{selectedUf && (
														<Dropdown.Item
															className="d-flex align-items-center gap-2"
															onClick={() => {
																setSelectedUser(userRow);
																handleConfirmRevoke(
																	userRow,
																	selectedUf || "",
																	selectedIbgeCode
																);
															}}
														>
															<FaBan className="text-muted" /> Remover
															acessos
														</Dropdown.Item>
													)}
												</Restrict>

												<Restrict>
													{userRow.isLocked ? (
														<Dropdown.Item
															onClick={() => {
																setSelectedUser(userRow);
																handleChangeAccess(userRow);
															}}
														>
															<FaUnlock className="text-success" />{" "}
															Desbloqueio
														</Dropdown.Item>
													) : (
														<Dropdown.Item
															onClick={() => {
																setSelectedUser(userRow);
																handleChangeAccess(userRow);
															}}
														>
															<FaLock className="text-danger" />{" "}
															Bloqueio
														</Dropdown.Item>
													)}
												</Restrict>
											</Dropdown.Menu>
										</Dropdown>
									</td>
								</tr>
							))}
						</Fragment>
					) : (
						isLoading && <TableSkeletons numberOfCells={7} />
					)}
				</tbody>
			</table>
			{paginatedUsers.totalRecords ? (
				<PaginationLinks
					itemsPerPage={paginatedUsers.pageSize}
					totalPages={paginatedUsers.totalRecords}
					changeSelectedPage={handleChangePageNumber}
					pageNumber={pageNumber || 0}
				/>
			) : (
				!isLoading &&
				paginatedUsers.totalRecords === 0 && (
					<NoRecordsFeedback
						message={"Nenhum Profissional encontrado."}
						icon={<FaUser />}
					/>
				)
			)}

			<ModalProfessionalInformation
				show={showModalProfessionalInformation}
				onHide={() => setShowModalProfessionalInformation(false)}
				selectedUser={selectedUser}
			/>

			<ModalProfessionalUnitAccess
				show={showModalProfessionalUnitAccess}
				onHide={() => setShowModalProfessionalUnitAccess(false)}
				selectedUser={selectedUser}
				setRefreshPage={() => refreshTable()}
			/>
		</>
	);
}
